<script setup lang="ts">
import { default as PInputText } from 'primevue/inputtext'
import { default as PInputNumber } from 'primevue/inputnumber'
import { default as PButton } from 'primevue/button'
import { default as PInputGroup } from 'primevue/inputgroup'
import { default as PInputGroupAddon } from 'primevue/inputgroupaddon'
import { default as PSelectButton } from 'primevue/selectbutton'
import { default as PFileUpload, type FileUploadUploaderEvent } from 'primevue/fileupload'
import { computed, ref } from 'vue'
import type { LearningGrain } from '@/core/domain/learning/grain/LearningGrain.ts'
import type { LearningSupport } from '@/core/domain/learning/support/LearningSupport.ts'
import { LearningSupportType } from '@/core/domain/resources/enums/LearningSupport.ts'
import { useLearningGrainsStore } from '@/presentation/stores/learning/learningGrains.ts'

const MAX_FILE_SIZE = 10 * 1024 * 1024

const learningGrainsStore = useLearningGrainsStore()

const learningGrain = defineModel<LearningGrain>('learningGrain', {
  required: true
})

const props = defineProps<{
  isEditing: boolean
  addSupport: () => void
  learningCourseId: number
  learningModuleId: number
}>()

const learningSupports = computed(() => learningGrain.value.learningSupports)

// Un fichier ne peut être rattaché qu'à un grain déjà enregistré (l'API refuse sinon)
const isGrainSaved = computed(() => learningGrain.value.id !== 0)

const typeOptions = computed(() => [
  {
    label: LearningSupportType.label(LearningSupportType.Values.GENIALLY),
    value: LearningSupportType.Values.GENIALLY,
    disabled: false
  },
  {
    label: LearningSupportType.label(LearningSupportType.Values.HTML),
    value: LearningSupportType.Values.HTML,
    disabled: !isGrainSaved.value
  }
])

const uploadingSupports = ref(new Set<LearningSupport>())

const lastSupportIsFilled = computed(() => {
  if (learningSupports.value && learningSupports.value.length > 0) {
    const lastSupport = learningSupports.value[learningSupports.value.length - 1]
    return lastSupport.isValid() && lastSupport.estimatedTime !== null
  }
  return true
})

function isUploading(support: LearningSupport): boolean {
  return uploadingSupports.value.has(support)
}

async function uploadFile(support: LearningSupport, event: FileUploadUploaderEvent) {
  const files = Array.isArray(event.files) ? event.files : [event.files]
  const file = files[0]
  if (!file) return

  uploadingSupports.value.add(support)
  try {
    const uploaded = await learningGrainsStore.uploadSupportFile(
      file,
      learningGrain.value.id,
      props.learningModuleId,
      props.learningCourseId
    )
    support.attachUploadedFile(uploaded)
  } catch {
    // Le store a déjà affiché l'erreur, le support garde son fichier précédent
  } finally {
    uploadingSupports.value.delete(support)
  }
}
</script>

<template>
  <h6 class="mt-4 mb-2">Liste des supports</h6>

  <template v-for="(support, index) in learningSupports" :key="`support_${index}`">
    <div class="flex gap-4 mb-2 justify-start w-full" data-test="support-row">
      <div
        class="flex-none"
        v-tooltip.top="
          isEditing && !isGrainSaved ? 'Enregistrez le grain avant d\'ajouter un fichier' : undefined
        "
      >
        <p-select-button
          :model-value="support.type"
          :options="typeOptions"
          option-label="label"
          option-value="value"
          option-disabled="disabled"
          :allow-empty="false"
          :disabled="!isEditing || isUploading(support)"
          data-test="support-type"
          @update:model-value="(type: LearningSupportType.Values) => support.changeType(type)"
        />
      </div>

      <div class="flex-1 min-w-0 flex flex-col gap-1">
        <p-input-group v-if="!support.isHtml()">
          <p-input-group-addon>
            <i class="pi pi-link" />
          </p-input-group-addon>
          <p-input-text
            class="w-full"
            :invalid="!support.isValid()"
            v-model="support.url"
            :disabled="!isEditing"
          />
        </p-input-group>

        <div v-else class="flex items-center gap-3 min-h-10" data-test="support-html">
          <template v-if="support.storageKey">
            <i class="pi pi-file" />
            <span data-test="support-file-name">
              {{ support.originalFileName ?? 'Fichier HTML' }}
              <template v-if="support.fileSize !== null">
                · {{ support.getFormattedFileSize() }}
              </template>
            </span>
            <a
              :href="support.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-primary underline"
              data-test="support-open"
            >
              Ouvrir
            </a>
          </template>

          <i v-if="isUploading(support)" class="pi pi-spin pi-spinner" data-test="support-uploading" />
          <p-file-upload
            v-else-if="isEditing"
            mode="basic"
            auto
            custom-upload
            accept=".html,.htm,text/html"
            :max-file-size="MAX_FILE_SIZE"
            invalid-file-size-message="Fichier trop volumineux (10 Mo maximum)"
            invalid-file-type-message="Le fichier doit avoir l'extension .html ou .htm"
            :choose-label="support.storageKey ? 'Remplacer' : 'Choisir un fichier HTML'"
            :class="{ 'p-invalid': !support.isValid() }"
            data-test="support-file-upload"
            @uploader="(event: FileUploadUploaderEvent) => uploadFile(support, event)"
          />
        </div>
      </div>

      <p-input-group class="flex-none w-36">
        <p-input-number
          class="w-full"
          min="0"
          :disabled="!isEditing"
          v-model="support.estimatedTime"
        />
        <p-input-group-addon>
          min.
        </p-input-group-addon>
      </p-input-group>

      <p-button
        class="flex-none"
        icon="pi pi-trash"
        severity="secondary"
        :disabled="!isEditing || isUploading(support)"
        @click="learningSupports.splice(index, 1)"
        text
      />
    </div>
  </template>

  <p-button
    class="mt-4"
    outlined
    v-if="isEditing"
    :disabled="!lastSupportIsFilled"
    label="Ajouter un nouveau support"
    @click="addSupport"
  />
</template>
